<template>
  <div>
    <card class="card-plain">
      <form @submit.prevent="handleSubmit">
        <div class="text-center form-heading mb-4">
          <h2>
            {{
              isEditMode ? "Edit Transaction Limit" : "Add Transaction Limit"
            }}
          </h2>
          <p>
            {{
              isEditMode
                ? "Edit existing transaction limit"
                : "Create new transaction limit"
            }}
          </p>
        </div>

        <div v-if="errorMessage || limitError" class="alert alert-danger">
          {{ errorMessage || limitError }}
        </div>

        <div>
          <label for="accountType">Account Type</label><br />
          <input
            v-if="isEditMode"
            disabled
            :value="formatText(formData.accountType)"
          />
          <select
            v-else
            v-model="formData.accountType"
            @change="toggleBusinessCategory"
          >
            <option value="individual-account">Individual</option>
            <option value="business-account">Business</option>
          </select>
        </div>

        <div v-if="formData.accountType === 'business-account'">
          <label for="businessCategory">Business Category</label><br />
          <input
            v-if="isEditMode"
            disabled
            :value="formatText(formData.businessCategory)"
          />
          <select v-else v-model="formData.businessCategory">
            <option value="sole-proprietor">Sole Proprietor</option>
            <option value="private-limited-liability-company">
              Private Limited Liability Company
            </option>
            <option value="public-limited-liability-company">
              Public Limited Liability Company
            </option>
            <option value="non-governmental-organization">
              Non-Government Organization
            </option>
            <option value="government">Government Body</option>
            <option value="professional-body">Professional Body</option>
          </select>
        </div>

        <div>
          <label for="tierLevel">Tier Level</label><br />
          <input
            v-if="formData.accountType === 'business-account'"
            disabled
            placeholder="Tier B"
          />
          <div v-else-if="formData.accountType === 'individual-account'">
            <input
              v-if="isEditMode"
              disabled
              :value="formatTier(formData.tierLevel)"
            />
            <select
              v-else
              v-model="formData.tierLevel"
              @change="onTierLevelChange"
            >
              <option value="tier_2">Tier 2</option>
              <option value="tier_3">Tier 3</option>
            </select>
          </div>
        </div>

        <div class="d-flex justify-content-between">
          <div class="input-flex">
            <label for="transferLimit"
              >Transfer Limit
              <span
                class="ti-info-alt"
                ref="transferTooltip"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Transfer limit capped at specified amount per transaction"
              ></span>
            </label>
            <input
              type="text"
              v-model="formData.transferLimit"
              @input="onTransferLimitChange"
              name="transferLimit"
            />
          </div>
          <div class="input-flex">
            <label for="dailyCumulativeLimit"
              >Daily Cumulative Limit
              <span
                class="ti-info-alt"
                ref="cumulativeTooltip"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Daily cumulative limit capped at specified amount per day"
              ></span> </label
            ><br />
            <input
              type="text"
              v-model="formData.dailyCumulativeLimit"
              @input="onCumulativeLimitChange"
              name="dailyCumulativeLimit"
            />
          </div>
        </div>

        <button
          type="submit"
          class="btn btn-primary mt-3"
          :disabled="isLoading || limitError || !isFormValid"
        >
          <span
            v-if="isLoading"
            class="spinner-grow"
            role="status"
            aria-hidden="true"
          ></span>
          {{ isLoading ? "Processing..." : isEditMode ? "Update" : "Create" }}
        </button>
      </form>
    </card>
  </div>
</template>

<script>
import axios from "../../store/axios";
import Swal from "sweetalert2";
import { Tooltip } from "bootstrap";

export default {
  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      formData: {
        accountType: "individual-account",
        businessCategory: "",
        tierLevel: "",
        transferLimit: "",
        dailyCumulativeLimit: "",
      },
      limitError: null,
      errorMessage: null,
      isLoading: false,
    };
  },

  computed: {
    isFormValid() {
      return (
        this.formData.accountType &&
        this.formData.transferLimit &&
        this.formData.dailyCumulativeLimit &&
        (this.formData.accountType !== "business-account" ||
          this.formData.businessCategory) &&
        (this.formData.accountType !== "individual-account" ||
          this.formData.tierLevel)
      );
    },
  },

  methods: {
    formatText(value) {
      if (!value) return "N/A";
      return value
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },

    formatTier(tierLevel) {
      if (!tierLevel) return null;
      return (
        tierLevel
          .replace(/_/g, " ")
          .replace(
            /(\w)(\w*)/,
            (match, first, rest) => first.toUpperCase() + rest,
          ) || "--"
      );
    },

    toggleBusinessCategory() {
      if (this.formData.accountType !== "business-account") {
        this.formData.businessCategory = "";
      }
      this.clearErrorsAfterDelay();
    },

    formatAmountForDisplay(value) {
      if (!value) return "";
      return parseFloat(value.replace(/,/g, "")).toLocaleString("en-US");
    },

    parseAmountForSubmission(value) {
      return parseFloat(String(value).replace(/,/g, "")) || 0;
    },

    onTierLevelChange(event) {
      this.formData.tierLevel = event.target.value;
      this.clearErrorsAfterDelay();
    },

    onTransferLimitChange(event) {
      const rawValue = String(event.target.value).replace(/,/g, "");
      this.formData.transferLimit = this.formatAmountForDisplay(rawValue);
      this.validateLimits();
      this.clearErrorsAfterDelay();
    },

    onCumulativeLimitChange(event) {
      const rawValue = String(event.target.value).replace(/,/g, "");
      this.formData.dailyCumulativeLimit =
        this.formatAmountForDisplay(rawValue);
      this.validateLimits();
      this.clearErrorsAfterDelay();
    },

    validateLimits() {
      const transferLimit =
        parseFloat(String(this.formData.transferLimit).replace(/,/g, "")) || 0;
      const dailyCumulativeLimit =
        parseFloat(
          String(this.formData.dailyCumulativeLimit).replace(/,/g, ""),
        ) || 0;
      if (
        this.formData.transferLimit !== "" &&
        this.formData.dailyCumulativeLimit !== ""
      ) {
        if (dailyCumulativeLimit <= transferLimit) {
          this.limitError =
            "Daily cumulative limit must be more than transfer limit";
        } else {
          this.limitError = null;
        }
      }
    },

    clearErrorsAfterDelay() {
      setTimeout(() => {
        this.errorMessage = null;
        this.limitError = null;
      }, 5000);
    },

    async handleSubmit() {
      this.isLoading = true;

      const submitData = {
        ...this.formData,
        transferLimit: parseFloat(
          String(this.formData.transferLimit).replace(/,/g, ""),
        ),
        dailyCumulativeLimit: parseFloat(
          String(this.formData.dailyCumulativeLimit).replace(/,/g, ""),
        ),
      };

      const endpoint = this.isEditMode
        ? `${process.env.VUE_APP_API_URL}/update-transaction-limit/${this.initialData._id}`
        : `${process.env.VUE_APP_API_URL}/create-transaction-limit`;

      try {
        await axios.post(endpoint, submitData);

        Swal.fire({
          title: this.isEditMode
            ? "Transaction Limit Updated"
            : "Transaction Limit Created",
          text: `You have successfully ${
            this.isEditMode ? "updated" : "added"
          } a transaction limit for ${this.formData.accountType}.`,
          icon: "success",
          confirmButtonText: "Done",
        }).then(() => {
          this.$emit("close");
          this.$emit("refresh");

          if (!this.isEditMode) {
            this.resetFormData();
          }
        });
      } catch (error) {
        this.errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "An error occurred. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },

    resetFormData() {
      this.formData = {
        accountType: "individual-account",
        businessCategory: "",
        tierLevel: "",
        transferLimit: "",
        dailyCumulativeLimit: "",
      };
      this.clearErrorsAfterDelay();
    },
  },

  mounted() {
    const tooltips = [this.$refs.transferTooltip, this.$refs.cumulativeTooltip];
    tooltips.forEach((tooltip) => {
      if (tooltip) new Tooltip(tooltip);
    });
  },

  watch: {
    initialData: {
      immediate: true,
      handler(newData) {
        if (this.isEditMode && newData) {
          this.formData = { ...newData };
        }
      },
    },

    formData: {
      deep: true,
      immediate: true,
      handler(newFormData) {
        const transferLimit =
          parseFloat(newFormData.transferLimit.replace(/,/g, "")) || 0;
        const dailyCumulativeLimit =
          parseFloat(newFormData.dailyCumulativeLimit.replace(/,/g, "")) || 0;

        if (
          newFormData.transferLimit !== "" ||
          newFormData.dailyCumulativeLimit !== ""
        ) {
          if (dailyCumulativeLimit <= transferLimit) {
            this.limitError =
              "Daily cumulative limit must be more than transfer limit";
          } else {
            this.limitError = null;
          }
        }
      },
    },
  },
};
</script>

<style scoped>
.form-heading h2 {
  font-weight: 700;
  font-size: 32px;
  margin: 0;
}

.form-heading p {
  color: #9497b8;
  margin-top: 10px;
}

label {
  color: #000 !important;
}

select,
input {
  width: 100%;
  border-radius: 4px;
  border: 1px solid #ced3dd;
  padding: 10px;
  margin-bottom: 15px;
}

.input-flex {
  width: 48%;
}

.btn {
  border-radius: 50px;
  height: 40px;
  background-color: #65b3c7 !important;
  border: none !important;
}

.ti-info-alt {
  font-size: 14px;
  font-weight: normal;
  color: #65b3c7;
}
</style>
